<script setup lang="ts">
import * as LucideIcons from 'lucide-vue-next';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import type { DynamicLayout } from '@/types/dynamic-layout';

import ScrollFlashContainer from '@/components/ScrollFlashContainer.vue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import DynamicSection from './DynamicSection.vue';

const props = defineProps<{
  layout: DynamicLayout;
}>();

const activeTabId = ref(props.layout.defaultTabId || props.layout.tabs[0]?.id || '');
const scrollContainerRef = ref<HTMLElement | null>(null);
const sectionRefs = ref<Record<string, HTMLElement>>({});
const observer = ref<IntersectionObserver | null>(null);
const isScrolling = ref(false);
const scrollTimer = ref<number | null>(null);

const availableTabs = computed(() => {
  return props.layout.tabs.map(tab => ({
    id: tab.id,
    label: tab.label,
    icon: tab.icon,
  }));
});

// Get Lucide icon component dynamically
function getIconComponent(iconName: string | undefined) {
  if (!iconName) return null;
  return (LucideIcons as any)[iconName] || null;
}

// 设置section元素的ref
function setSectionRef(tabId: string, el: HTMLElement | null) {
  if (el) {
    sectionRefs.value[tabId] = el;
  } else {
    delete sectionRefs.value[tabId];
  }
}

// 平滑滚动到指定section
function scrollToSection(tabId: string) {
  const targetElement = sectionRefs.value[tabId];
  if (!targetElement || !scrollContainerRef.value) return;

  // 设置滚动状态
  isScrolling.value = true;
  if (scrollTimer.value) {
    clearTimeout(scrollTimer.value);
  }

  // 获取ScrollFlashContainer内部的实际滚动容器
  const scrollFlashInstance = scrollContainerRef.value as any;
  const actualScrollContainer = scrollFlashInstance?.containerRef;

  if (!actualScrollContainer || typeof actualScrollContainer.scrollTo !== 'function') {
    console.warn('无法找到可滚动的容器元素', {
      actualScrollContainer,
      scrollFlashInstance,
      hasContainerRef: !!scrollFlashInstance?.containerRef,
      containerRefType: typeof scrollFlashInstance?.containerRef,
    });
    isScrolling.value = false;
    return;
  }

  console.log('成功找到滚动容器，准备滚动到:', tabId);

  // 计算滚动位置 - 考虑Tab栏的高度
  const tabsHeight = 60; // Tab栏大概高度
  const targetTop = targetElement.offsetTop - tabsHeight - 16; // 额外16px间距

  actualScrollContainer.scrollTo({
    top: Math.max(0, targetTop),
    behavior: 'smooth',
  });

  // 设置滚动完成定时器
  scrollTimer.value = setTimeout(() => {
    isScrolling.value = false;
    activeTabId.value = tabId;
  }, 500) as unknown as number;
}

// 手动点击Tab时的处理
function handleTabClick(tabId: string) {
  if (tabId === activeTabId.value) return;

  scrollToSection(tabId);
}

// 设置Intersection Observer来检测当前可见的section
function setupIntersectionObserver() {
  if (!scrollContainerRef.value) return;

  // 获取ScrollFlashContainer内部的实际滚动容器
  const scrollFlashInstance = scrollContainerRef.value as any;
  const actualScrollContainer = scrollFlashInstance?.containerRef;

  if (!actualScrollContainer) {
    console.warn('无法找到可滚动的容器元素用于Intersection Observer');
    return;
  }

  observer.value = new IntersectionObserver(
    entries => {
      if (isScrolling.value) return; // 滚动过程中不更新activeTab

      // 找到当前最可见的section
      let maxVisibleRatio = 0;
      let mostVisibleTabId = '';

      entries.forEach(entry => {
        const tabId = entry.target.getAttribute('data-tab-id');
        if (!tabId) return;

        if (entry.isIntersecting && entry.intersectionRatio > maxVisibleRatio) {
          maxVisibleRatio = entry.intersectionRatio;
          mostVisibleTabId = tabId;
        }
      });

      // 更新活跃Tab
      if (mostVisibleTabId && mostVisibleTabId !== activeTabId.value) {
        activeTabId.value = mostVisibleTabId;
      }
    },
    {
      root: actualScrollContainer,
      rootMargin: '-20% 0px -60% 0px', // 上方20%，下方60%的区域内被认为是"可见"
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
    },
  );

  // 观察所有section
  Object.values(sectionRefs.value).forEach(sectionEl => {
    if (observer.value && sectionEl) {
      observer.value.observe(sectionEl);
    }
  });
}

// 清理资源
function cleanup() {
  if (observer.value) {
    observer.value.disconnect();
    observer.value = null;
  }

  if (scrollTimer.value) {
    clearTimeout(scrollTimer.value);
    scrollTimer.value = null;
  }
}

// 监听sectionRefs变化，重新设置observer
watch(
  () => sectionRefs.value,
  () => {
    nextTick(() => {
      cleanup();
      setupIntersectionObserver();
    });
  },
  { deep: true },
);

onMounted(() => {
  console.log('DynamicLayout mounted, layout.tabs:', props.layout.tabs?.length);
  console.log('Available tabs:', availableTabs.value);
  nextTick(() => {
    setupIntersectionObserver();
  });
});

onUnmounted(() => {
  cleanup();
});
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Fixed Tab Bar -->
    <div class="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b">
      <div class="py-2">
        <div class="flex bg-muted p-1 rounded-lg w-full">
          <TooltipProvider>
            <Tooltip v-for="tab in availableTabs" :key="tab.id">
              <TooltipTrigger as-child>
                <button
                  class="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200"
                  :class="[
                    activeTabId === tab.id
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-background/50',
                  ]"
                  @click="handleTabClick(tab.id)"
                >
                  <component
                    :is="getIconComponent(tab.icon)"
                    v-if="tab.icon && getIconComponent(tab.icon)"
                    class="h-4 w-4 flex-shrink-0"
                  />
                  <span class="truncate">{{ tab.label }}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{{ tab.label }}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>

    <!-- Scrollable Content Container with ScrollFlashContainer -->
    <div class="flex-1 min-h-0 relative">
      <ScrollFlashContainer
        ref="scrollContainerRef"
        :flash-count="3"
        :flash-interval="150"
        :flash-on-init="true"
        class="h-[calc(100vh-260px)] overflow-y-auto"
      >
        <div>
          <!-- All Tab Contents Combined -->
          <template v-for="(tab, index) in layout.tabs" :key="tab.id">
            <div
              :ref="el => setSectionRef(tab.id, el as HTMLElement)"
              :data-tab-id="tab.id"
              class="scroll-mt-20"
              :class="[{ 'mt-8': index > 0 }]"
            >
              <!-- Tab Section Title -->
              <div class="mb-6">
                <div class="flex items-center gap-3 mb-4 mt-4 ml-[24px]">
                  <!-- <component :is="getIconComponent(tab.icon)" v-if="tab.icon && getIconComponent(tab.icon)"
                    class="h-6 w-6 text-primary" /> -->
                  <h2 class="text-2xl font-semibold text-foreground">
                    {{ tab.label }}
                  </h2>
                </div>
                <div class="h-px bg-gradient-to-r from-primary/20 via-primary/10 to-transparent" />
              </div>

              <!-- Tab Sections -->
              <div class="space-y-6">
                <DynamicSection
                  v-for="section in tab.sections"
                  :key="section.id"
                  :section="section"
                />
              </div>
            </div>
          </template>
        </div>
      </ScrollFlashContainer>
    </div>
  </div>
</template>
